import { ProposalFieldJSON } from "work/proposals/proposal-fields/_schema/proposal-field-schema";
import ProposalField from "work/proposals/proposal-fields/proposal-field";
import { TextChangeJSON } from "work/proposals/redlining/__diff/_schema/text-change-schema";
import TextChange from "work/proposals/redlining/__diff/text-change";
import RedlineChangeSchema from "work/proposals/redlining/changes/_schemas/redline-change-schema";

export interface RedlineChangeJSON {
  field: ProposalFieldJSON;
  isResolved: boolean;
  action: RedlineActionKey;
  textChanges: TextChangeJSON[];
}

export class RedlineChange {
  public readonly field: ProposalField;
  public readonly isResolved;
  public readonly action;
  public readonly textChanges;

  constructor(args: { 
    field: ProposalField; 
    isResolved: boolean; 
    action: RedlineAction; 
    textChanges: TextChange[] 
  }) {
    this.field = args.field;
    this.isResolved = args.isResolved;
    this.action = args.action;
    this.textChanges = args.textChanges;
  }

  public static fromJSON(params: { json: RedlineChangeJSON; field: ProposalField }): RedlineChange {
    const { json, field } = params;
    const args = RedlineChangeSchema.deserialize(json);
    if (!args.action) {
      throw new Error("Redline change action is required in fromJSON.");
    }
    return new RedlineChange({
      ...args,
      field,
    });
  }

  public toJSON(): RedlineChangeJSON {
    return RedlineChangeSchema.serialize(this);
  }
}

export class RedlineAction {
  // The present tense of the action MUST match the readonly property name
  public static readonly Accept: RedlineAction = new RedlineAction("Accept", "Accepted");
  public static readonly Reject: RedlineAction = new RedlineAction("Reject", "Rejected");
  public static readonly Undo: RedlineAction = new RedlineAction("Undo", "Undid");
  public static readonly UndoAdd: RedlineAction = new RedlineAction("UndoAdd", "Undid addition of");
  public static readonly UndoEdit: RedlineAction = new RedlineAction("UndoEdit", "Undid edit");
  public static readonly UndoRemove: RedlineAction = new RedlineAction("UndoRemove", "Undid removal of");
  public static readonly UndoAccept: RedlineAction = new RedlineAction("UndoAccept", "Undid acceptance of");
  public static readonly UndoReject: RedlineAction = new RedlineAction("UndoReject", "Undid rejection of");
  public static readonly Edit: RedlineAction = new RedlineAction("Edit", "Edited");
  public static readonly ReEdit: RedlineAction = new RedlineAction("ReEdit", "Re-edited");
  public static readonly Add: RedlineAction = new RedlineAction("Add", "Added");
  public static readonly Remove: RedlineAction = new RedlineAction("Remove", "Removed");

  public static fromJSON(obj: keyof typeof RedlineAction): RedlineAction {
    const action = RedlineAction[obj] as RedlineAction;
    if (!action) {
      throw new Error(`Invalid RedlineAction key '${obj}' provided to RedlineAction.fromJSON.`);
    }
    return action;
  }

  public constructor(
    private readonly presentTense: string, 
    public readonly actionDescription: string
  ) {}

  public toString(): string {
    return this.presentTense;
  }

  public isEqualTo(other: RedlineAction | null | undefined): boolean {
    if (!other) return false;
    return this.presentTense === other.presentTense;
  }
}

export type RedlineActionKey =
  | "Accept"
  | "Reject"
  | "Undo"
  | "UndoAdd"
  | "UndoEdit"
  | "UndoRemove"
  | "UndoAccept"
  | "UndoReject"
  | "Edit"
  | "ReEdit"
  | "Add"
  | "Remove";
